import { type ComponentType, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

/**
 * Delivery status as reported by the API.
 *
 * `executionState` is set by the system and is one of RUNNING, READY, NOT_READY, ENDED.
 * `userState` is set by the user and is one of PROPOSED, SUBMITTED, RESERVED,
 * APPROVED, PAUSED, STOPPED, COMPLETED. Neither field alone tells the whole
 * story — a paused line item can still report READY — so `deliveryStatusOf`
 * folds them into the single status we show in lists.
 */
export type DeliveryStatus =
  | 'RUNNING'
  | 'READY'
  | 'NOT_READY'
  | 'ENDED'
  | 'PAUSED'
  | 'STOPPED'
  | 'COMPLETED'
  | 'PROPOSED'
  | 'SUBMITTED'
  | 'RESERVED'
  | 'APPROVED'

export type Tone = 'green' | 'blue' | 'amber' | 'red' | 'gray'

export const TONE_CLASSES: Record<Tone, string> = {
  green: 'bg-(--ad-badge-green-bg) text-(--ad-badge-green) border-(--ad-badge-green-border)',
  blue:  'bg-(--ad-badge-blue-bg) text-(--ad-badge-blue) border-(--ad-badge-blue-border)',
  amber: 'bg-(--ad-badge-amber-bg) text-(--ad-badge-amber) border-(--ad-badge-amber-border)',
  red:   'bg-(--ad-badge-red-bg) text-(--ad-badge-red) border-(--ad-badge-red-border)',
  gray:  'bg-(--ad-badge-gray-bg) text-(--ad-badge-gray) border-(--ad-badge-gray-border)',
}

/** Delivering is green, anything blocked or stopped is red, in-between is blue/amber. */
const STATUS_TONES: Record<DeliveryStatus, Tone> = {
  RUNNING:   'green',
  READY:     'blue',
  SUBMITTED: 'blue',
  RESERVED:  'blue',
  APPROVED:  'green',
  PAUSED:    'amber',
  NOT_READY: 'red',
  STOPPED:   'red',
  ENDED:     'gray',
  COMPLETED: 'gray',
  PROPOSED:  'gray',
}

/** The user state overrides execution state when the user has held delivery back. */
const USER_STATE_OVERRIDES: DeliveryStatus[] = ['STOPPED', 'COMPLETED', 'PAUSED', 'PROPOSED', 'SUBMITTED', 'RESERVED']

/** Folds the API's `executionState` and `userState` into one delivery status. */
export function deliveryStatusOf(item: { executionState?: string | null; userState?: string | null }): string {
  const userState = item.userState ?? ''
  if (USER_STATE_OVERRIDES.includes(userState as DeliveryStatus)) return userState
  return item.executionState ?? ''
}

function humanize(state: string) {
  return state.replace(/_/g, ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase())
}

/**
 * The pill every status wears: a coloured dot and a short label. Shared so a
 * delivery status and a pacing verdict sit side by side as the same object.
 */
export type StatusPillSize = 'sm' | 'md'

const SIZE_CLASSES: Record<StatusPillSize, string> = {
  sm: 'gap-1.5 px-2 py-0.5 text-[11px]',
  md: 'gap-1.5 px-3 py-1 text-sm',
}

export function StatusPill({
  tone,
  pulse,
  size = 'sm',
  icon: Icon,
  trailing,
  children,
}: {
  tone: Tone
  /** Marks a status that is happening right now rather than resting. */
  pulse?: boolean
  size?: StatusPillSize
  /** Replaces the dot with an icon. */
  icon?: ComponentType<{ className?: string }>
  /** Sits after the label, inside the pill — e.g. a menu caret. */
  trailing?: ReactNode
  children: ReactNode
}) {
  return (
    <span
      className={`inline-flex w-fit shrink-0 items-center rounded-md border font-semibold whitespace-nowrap ${SIZE_CLASSES[size]} ${TONE_CLASSES[tone]}`}
    >
      {Icon ? (
        // text-current keeps the tone colour where a parent (e.g. a menu item) tints svgs.
        <Icon className={`shrink-0 text-current ${size === 'md' ? 'size-4' : 'size-3'}`} />
      ) : (
        <span className={`size-1.5 shrink-0 rounded-full bg-current ${pulse ? 'animate-pulse' : ''}`} />
      )}
      {children}
      {trailing}
    </span>
  )
}

/** Colour-coded delivery status pill for line items and orders. */
export function DeliveryStatusBadge({
  status,
  size,
  icon,
  trailing,
}: {
  status?: string | null
  size?: StatusPillSize
  icon?: ComponentType<{ className?: string }>
  trailing?: ReactNode
}) {
  const { t } = useTranslation()
  if (!status) return <span className="text-xs text-(--ad-text-muted)">—</span>

  const tone = STATUS_TONES[status as DeliveryStatus] ?? 'gray'
  const label = t(`deliveryStatuses.${status}`, { defaultValue: humanize(status) })

  return (
    <StatusPill tone={tone} pulse={status === 'RUNNING'} size={size} icon={icon} trailing={trailing}>
      {label}
    </StatusPill>
  )
}
